import { fallbackLng, languages } from "@/i18n/settings";

const BRAZIL_COUNTRY_CODE = "BR";
const PORTUGUESE_LANGUAGE = "pt";
const QAE_PORTUGUESE_LANGUAGE = "pt-BR";
const ENGLISH_LANGUAGE = "en";
const SPANISH_LANGUAGE = "es";
const ABSOLUTE_HTTP_URL_PATTERN = /^https?:\/\//i;

interface ResolveCandidateLanguageParams {
  manualLanguage?: string | null;
  candidateLanguage?: string | null;
  companyCountry?: string | null;
  personCountry?: string | null;
  acceptLanguage?: string | null;
}

function normalizeLanguage(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase().replace(/_/g, "-");

  if (normalized === "pt" || normalized === "pt-br") {
    return PORTUGUESE_LANGUAGE;
  }

  if (normalized.startsWith("en")) {
    return ENGLISH_LANGUAGE;
  }

  if (normalized.startsWith("es")) {
    return SPANISH_LANGUAGE;
  }

  return null;
}

export function isSupportedLanguage(value?: string | null): boolean {
  const normalizedLanguage = normalizeLanguage(value);
  if (!normalizedLanguage) {
    return false;
  }

  return languages.includes(normalizedLanguage);
}

export function getSupportedLanguage(value?: string | null): string {
  const normalizedLanguage = normalizeLanguage(value);
  if (!normalizedLanguage) {
    return fallbackLng;
  }

  if (!languages.includes(normalizedLanguage)) {
    return fallbackLng;
  }

  return normalizedLanguage;
}

export function getPathLanguage(pathname: string): string | null {
  const normalizedPathname = pathname || "";

  for (const language of languages) {
    if (normalizedPathname === `/${language}`) {
      return language;
    }

    if (normalizedPathname.startsWith(`/${language}/`)) {
      return language;
    }
  }

  return null;
}

export function stripLanguagePrefix(pathname: string): string {
  if (!pathname) {
    return "";
  }

  const pathLanguage = getPathLanguage(pathname);
  if (!pathLanguage) {
    return pathname;
  }

  if (pathname === `/${pathLanguage}`) {
    return "/";
  }

  return pathname.slice(pathLanguage.length + 1);
}

export function isAbsoluteHttpUrl(path: string): boolean {
  return ABSOLUTE_HTTP_URL_PATTERN.test(path);
}

export function buildLocalizedPath(path: string, language: string, showLocale = false): string {
  if (isAbsoluteHttpUrl(path)) {
    return path;
  }

  const supportedLanguage = getSupportedLanguage(language);
  let normalizedPath = path;
  if (!normalizedPath.startsWith("/")) {
    normalizedPath = `/${normalizedPath}`;
  }
  const cleanPath = stripLanguagePrefix(normalizedPath);

  if (supportedLanguage === fallbackLng && !showLocale) {
    return cleanPath;
  }

  if (cleanPath === "/") {
    return `/${supportedLanguage}`;
  }

  return `/${supportedLanguage}${cleanPath}`;
}

export function resolveCandidateLanguage(params: ResolveCandidateLanguageParams): string {
  const manualLanguage = normalizeLanguage(params.manualLanguage);
  if (manualLanguage) {
    return manualLanguage;
  }

  const candidateLanguage = normalizeLanguage(params.candidateLanguage);
  if (candidateLanguage) {
    return candidateLanguage;
  }

  if (params.companyCountry === BRAZIL_COUNTRY_CODE) {
    return PORTUGUESE_LANGUAGE;
  }

  if (params.personCountry === BRAZIL_COUNTRY_CODE) {
    return PORTUGUESE_LANGUAGE;
  }

  const acceptLanguage = normalizeLanguage(params.acceptLanguage);
  if (acceptLanguage) {
    return acceptLanguage;
  }

  return fallbackLng;
}

export function getStoredLanguage(): string {
  if (typeof window === "undefined") {
    return fallbackLng;
  }

  return getSupportedLanguage(localStorage.getItem("i18nextLng"));
}

export function normalizeQaeLanguage(value?: string | null): "es" | "pt-BR" {
  const normalized = normalizeLanguage(value);
  if (normalized === PORTUGUESE_LANGUAGE) {
    return QAE_PORTUGUESE_LANGUAGE;
  }

  return SPANISH_LANGUAGE;
}
